/**
 * MusicCrossNav — 3 side-by-side links connecting every music surface to the
 * other two: Featured Artists · Whole Community Calendar · For Bands.
 *
 * Drop at the top of any music-related page (MusicScene, FeaturedArtistsPage,
 * BandsLandingPage, ForBandsPage, BandTourPage). When the visitor is already
 * on one of the destinations, that pill renders as "current" (dimmed, not a
 * link) — the other two stay actionable.
 */
import { Link, useLocation } from "react-router-dom";
import { Calendar, Music, Sparkles, Mic2 } from "lucide-react";

const LINKS = [
  {
    to: "/featured-artists",
    matchPaths: ["/featured-artists", "/artists/featured"],
    label: "Featured Artists",
    icon: Sparkles,
    accent: "from-[#FFB347] to-[#E76F51]",
    testid: "music-xnav-featured-artists",
  },
  {
    to: "/events",
    matchPaths: ["/events"],
    // exact match for the calendar page only — /events/music is the music
    // sub-calendar, not the "whole community calendar"
    exact: true,
    label: "Whole Community Calendar",
    icon: Calendar,
    accent: "from-[#2E86AB] to-[#1A1A2E]",
    testid: "music-xnav-community-calendar",
  },
  {
    to: "/for-bands",
    matchPaths: ["/for-bands"],
    label: "For Bands",
    icon: Mic2,
    accent: "from-[#9B59B6] to-[#3a2056]",
    testid: "music-xnav-for-bands",
  },
];

const isCurrent = (link, pathname) => {
  if (link.exact) return pathname === link.to;
  return link.matchPaths.some(p => pathname === p || pathname.startsWith(`${p}/`));
};

const MusicCrossNav = ({ className = "", variant = "light" }) => {
  const { pathname } = useLocation();
  const isDark = variant === "dark";

  return (
    <nav
      data-testid="music-cross-nav"
      className={`flex flex-wrap items-stretch gap-2 sm:gap-3 ${className}`}
      aria-label="Music section navigation"
    >
      {LINKS.map(({ to, label, icon: Icon, accent, testid, ...rest }) => {
        const current = isCurrent({ to, ...rest }, pathname);
        const base =
          "flex-1 min-w-[140px] sm:min-w-[180px] flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl text-[11px] sm:text-xs font-bold uppercase tracking-wider transition-all no-underline";

        if (current) {
          return (
            <span
              key={to}
              data-testid={`${testid}-current`}
              aria-current="page"
              className={`${base} ${
                isDark
                  ? "bg-white/10 text-white/60 ring-1 ring-white/15 cursor-default"
                  : "bg-[#F4F2EF] text-[#9A9A9A] ring-1 ring-[#E2E0DC] cursor-default"
              }`}
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{label}</span>
              <span className="text-[9px] font-semibold opacity-70">· you are here</span>
            </span>
          );
        }

        return (
          <Link
            key={to}
            to={to}
            data-testid={testid}
            className={`${base} text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 bg-gradient-to-br ${accent}`}
          >
            <Icon className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{label}</span>
          </Link>
        );
      })}
    </nav>
  );
};

// Re-export the Music icon for callers that want to badge the nav externally.
export { Music };
export default MusicCrossNav;
